import { EFFECTIVE_DATE, VENUE_COUNTY } from "@/lib/legalConfig";
import { PUCT_REGISTRATION_VERIFIED, REGISTERED_BROKER_LEGAL_LINE } from "@/lib/brokerRegistration";

// Source: AIOpCo Docs & Templates/legal/Terms_Of_Use.md (Witherspoon-CLO draft). Internal
// "Publication Notes" and [VERIFY] tags are deliberately NOT carried over. Inline markup:
// **bold**, [text](href). The bare address support@ratecurrent.com auto-links.
export const TERMS = {
  title: "Terms Of Use",
  effectiveDate: EFFECTIVE_DATE,
  lastUpdated: EFFECTIVE_DATE,
  shortVersion: [
    "Today, RateCurrent teaches. It does not advise, recommend, or act for you with any provider.",
    "The bill explainer is an automated estimate. It can be wrong. Check your real bill.",
    "Use the tools for yourself. No bots, no scraping, no uploading other people's bills.",
    "We provide the Services as they are, and our liability is limited as far as Texas law allows.",
    "Texas law governs. Questions: **support@ratecurrent.com**.",
  ],
  sections: [
    {
      id: "agreement",
      heading: "1. Agreement",
      blocks: [
        { t: "p", text: "These Terms are a contract between you and RateCurrent LLC, a Texas limited liability company (\"RateCurrent,\" \"we,\" \"us\"). They cover ratecurrent.com and its tools (the \"Services\"). By using the Services, you agree to these Terms and our [Privacy Policy](/privacy). If you do not agree, do not use the Services." },
        { t: "p", text: "You must be at least 18 years old and able to form a contract." },
      ],
    },
    {
      id: "what-we-are",
      heading: "2. What RateCurrent Is, And Is Not",
      blocks: [
        { t: "p", text: "**Today, RateCurrent offers two tools:**" },
        {
          t: "ul",
          items: [
            "**ESID Lookup.** Find the ESID (meter ID) and local utility for a Texas address.",
            "**Bill Check.** Upload your electric bill and get a plain-language explanation of it.",
          ],
        },
        { t: "quote", text: "Plan comparison and switching are coming soon. Today, RateCurrent helps Texas electricity shoppers look up their meter's ESID and understand their electric bill." },
        { t: "p", text: "**What that means for you today:**" },
        {
          t: "ul",
          items: [
            "The Services are **educational only**. They explain information. They are not advice.",
            "RateCurrent **does not recommend, rank, or sell** electricity plans or providers.",
            "RateCurrent **does not enter you into any plan or contract** with any provider, and does not act on your behalf with any provider.",
            "RateCurrent **does not offer** legal, financial, tax, or energy-consulting advice.",
          ],
        },
        { t: "p", text: "RateCurrent is not a retail electric provider or a utility. We do not generate, sell, deliver, or bill for electricity. RateCurrent is not affiliated with or endorsed by the Public Utility Commission of Texas, ERCOT, or any Texas utility." },
        { t: "p", text: "Any decision about your electricity service is yours. Check your provider's Electricity Facts Label and terms before you act." },
      ],
    },
    {
      id: "accuracy",
      heading: "3. Accuracy: Read This Part",
      blocks: [
        { t: "p", text: "**The Bill Check result is an automated estimate, not your bill.** Our AI service provider reads your upload and does its best to identify your rate, usage, and provider. It can misread a scan or a photo, miss a charge, or word something poorly." },
        {
          t: "ul",
          items: [
            "The explanation is for your convenience only. It is not a substitute for your actual bill.",
            "**RateCurrent does not guarantee** that any number, name, or statement in a result is complete or correct.",
            "ESID Lookup results come from ERCOT public registry data. The registry can be out of date, can list old or inactive meters, and can be missing addresses. Check your ESID against your bill.",
            "Information on the Services is general. Your situation may differ.",
            "If a result looks wrong, tell us at support@ratecurrent.com. We cannot promise a correction, but we read every message.",
          ],
        },
      ],
    },
    {
      id: "using-the-services",
      heading: "4. Using The Services",
      blocks: [
        { t: "p", text: "**You may:**" },
        { t: "ul", items: ["Use the Services for yourself, for personal, non-commercial purposes.", "Link to our pages."] },
        { t: "p", text: "**You may not:**" },
        {
          t: "ul",
          items: [
            "Use bots, scrapers, crawlers, or other automated tools to collect data or content from the Services, or to submit lookups or uploads in bulk. Search engines that follow our robots rules are fine.",
            "Copy ERCOT-based results in bulk, or build a database or competing service from them.",
            "Get around, test, or attack our security, rate limits, or the bot check (Turnstile).",
            "Send malware or anything that harms the Services or other users.",
            "Overload or disrupt the Services, or use them to harm anyone.",
            "Pretend to be someone else, or use another person's information without their permission.",
            "Use the Services to break the law or to mislead anyone about a provider, a utility, or us.",
            "Say or imply that RateCurrent endorses you or your product.",
            "Resell or commercially use the Services without our written permission.",
          ],
        },
        { t: "p", text: "We limit how many lookups and uploads each visitor can run, to keep the Services running for everyone. We may block or slow access, or end your access, if we believe these Terms are being broken." },
      ],
    },
    {
      id: "uploads",
      heading: "5. Uploads",
      blocks: [
        { t: "p", text: "**What you may upload:** your own electric bill, or a bill you are authorized to upload by the account holder. PDF, JPEG, or PNG, up to 15 MB." },
        { t: "p", text: "**What you may not upload:**" },
        {
          t: "ul",
          items: [
            "Anything that is not an electric bill.",
            "Another person's bill or confidential information without their permission.",
            "Social Security numbers, payment card numbers, or bank details. If they appear on your bill, cover them first.",
            "Anything illegal, harmful, or infected with malware.",
          ],
        },
        { t: "p", text: "**Your promises.** When you tick the consent box, you confirm that the bill is yours or that you are authorized to upload it, and you authorize us to send it to our AI service provider to read and explain it." },
        { t: "p", text: "**Your rights stay yours.** You own your bill. You give us permission to process it as described here and in the Privacy Policy, only to run Bill Check for you." },
        { t: "p", text: "**What happens to it.** The [Privacy Policy](/privacy) explains this in detail. In short, we do not save your bill file, and we do not use it to train any AI model." },
      ],
    },
    {
      id: "accounts",
      heading: "6. Accounts (If You Create One)",
      blocks: [
        { t: "p", text: "Some features may let you create an account with an email address and password. Give us accurate information, keep your password private, and tell us right away if you think someone else used your account. You are responsible for activity under your account. We may close accounts that break these Terms. You can close yours by emailing support@ratecurrent.com." },
      ],
    },
    {
      id: "email",
      heading: "7. Email",
      blocks: [
        { t: "p", text: "If you choose to get email updates, you do that through a separate box. It is not part of these Terms. Every email can be unsubscribed from. See the [Privacy Policy](/privacy)." },
        { t: "p", text: "How we handle personal information, including whether and when we may share it or be paid for it, is set out in the [Privacy Policy](/privacy). These Terms are not your consent to any sale or sharing of information that identifies you. If we offer that, we ask you separately." },
      ],
    },
    {
      id: "our-content",
      heading: "8. Our Content And Your Feedback",
      blocks: [
        { t: "p", text: "The Services, including the RateCurrent name, logo, design, software, text, and compiled content, belong to RateCurrent LLC or its providers and are protected by copyright, trademark, and other law. We give you a limited, revocable, non-transferable right to use the Services under these Terms. We keep all other rights." },
        { t: "p", text: "Public data we show (such as ERCOT registry data) stays subject to the rules of its source." },
        { t: "p", text: "If you send us feedback or ideas, we may use them without owing you anything or crediting you." },
        { t: "p", text: "Third-party names and marks belong to their owners. Their use here does not mean they endorse us." },
      ],
    },
    {
      id: "other-sites",
      heading: "9. Other Sites And Providers",
      blocks: [
        { t: "p", text: "Our Services rely on third-party providers (for example, an AI service provider and hosting companies). We are not responsible for their outages or errors." },
      ],
    },
    {
      id: "how-we-may-be-paid",
      heading: "10. How RateCurrent May Be Paid",
      blocks: [
        { t: "p", text: "RateCurrent may receive payment from retail electric providers, for example when you enroll with a provider through a link or form on the Services. Which providers and plans appear on the Services can depend on whether we have an arrangement with them, so we may not show every provider or plan available to you. Plans, prices, and terms are set by the providers. Read the provider's Electricity Facts Label before you enroll." },
      ],
    },
    {
      id: "disclaimers",
      heading: "11. Disclaimers",
      blocks: [
        { t: "p", text: "**THE SERVICES ARE PROVIDED \"AS IS\" AND \"AS AVAILABLE.\" TO THE FULLEST EXTENT TEXAS LAW ALLOWS, RATECURRENT DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, AND NON-INFRINGEMENT.**" },
        { t: "p", text: "**We do not promise that the Services will be uninterrupted, error-free, or secure, or that any result will be complete, current, or right for your situation.** We are not responsible for any decision you make based on the Services." },
        { t: "p", text: "Some laws do not allow certain disclaimers. If those laws apply to you, our disclaimers apply to the extent allowed." },
      ],
    },
    {
      id: "limit-of-liability",
      heading: "12. Limit Of Liability",
      blocks: [
        { t: "p", text: "**TO THE FULLEST EXTENT TEXAS LAW ALLOWS:**" },
        {
          t: "ul",
          items: [
            "**RATECURRENT LLC, ITS OWNERS, MANAGERS, EMPLOYEES, AND PROVIDERS WILL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS, LOST DATA, OR HIGHER ELECTRICITY COSTS, ARISING FROM YOUR USE OF THE SERVICES.**",
            "**OUR TOTAL LIABILITY FOR ANY CLAIM RELATED TO THE SERVICES WILL NOT EXCEED $100.**",
          ],
        },
        { t: "p", text: "Nothing in these Terms limits liability that cannot be limited under law, including liability for fraud or willful misconduct, or any consumer right that cannot be waived." },
      ],
    },
    {
      id: "governing-law",
      heading: "13. Governing Law And Disputes",
      blocks: [
        { t: "p", text: `Texas law governs these Terms and any dispute about the Services, without regard to conflict-of-law rules. Any lawsuit must be filed in the state or federal courts located in ${VENUE_COUNTY} County, Texas, and you and we consent to those courts.` },
        { t: "p", text: "Before filing a claim, please email support@ratecurrent.com and give us 30 days to work it out." },
      ],
    },
    {
      id: "general",
      heading: "14. Changes, Ending, And General Terms",
      blocks: [
        {
          t: "ul",
          items: [
            "**Changes.** We may update these Terms. We will post the new version and change the \"Last Updated\" date. For material changes, we will also put a notice on the site for at least 30 days before they take effect. If you keep using the Services after that, you accept the update.",
            "**Changes to the Services.** We may change, pause, or stop any part of the Services at any time. Features marked \"coming soon\" are plans, not promises, and have no set date.",
            "**Ending.** You may stop using the Services at any time. We may suspend or end your access at any time, including for breaking these Terms. Sections that by nature should survive (accuracy, uploads, our content, disclaimers, liability, governing law) do.",
            "**Electronic notices.** We may give you notices by posting on the Services or by email.",
            "**If part is invalid.** If a court finds part of these Terms unenforceable, the rest stays in effect.",
            "**Entire agreement.** These Terms and the Privacy Policy are the whole agreement between you and us about the Services. You may not transfer your rights under them. We may.",
            "**No waiver.** If we do not enforce a term, we have not given it up.",
          ],
        },
      ],
    },
    {
      id: "contact",
      heading: "15. Contact",
      blocks: [
        { t: "address", lines: ["RateCurrent LLC", "Email: support@ratecurrent.com"] },
        // CLO Registered_Broker_Wording.md 4.2 (S3): the only page that carries the registration number.
        ...(PUCT_REGISTRATION_VERIFIED ? [{ t: "p", text: REGISTERED_BROKER_LEGAL_LINE }] : []),
      ],
    },
  ],
};
